import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import sendEvent from '~actions';
import updateDataLayer from '~utils/events';
import {
  useStateName,
  useStateField,
  useStateCode,
} from '~state/getStateField';
import PropTypes from 'prop-types';
import FuturevoterEligibleContent, {
  FuturevoterEligibleHeader,
} from '~components/Futurevoter/FuturevoterEligibleContent';
import { registrationNoStateFormAvailable } from '~components/Register/specialCases';
import { navigate } from 'gatsby';
import urlToPrimaryDomain from '~utils/urlToPrimaryDomain';
import { CustomizableLink } from '~components/common/Link';
import SameDayRegistrationAlert from '~components/common/SameDayRegistrationAlert';
import AlertBar from '~components/common/AlertBar';
import Markdown from '../common/Markdown';
import { CustomizableButton, ButtonGroup } from '../common/Button';
import ExternalLinkIcon from '../common/ExternalLinkIcon';
import ContentDiv from '../common/ContentDiv';
import { sendRegistrationForm } from './postFinishEvents';
import useRegisterRedirect from './useRegisterRedirect';

function ProceedWithStateContent({
  ovrLink,
  toggleClickExternal,
  setSubmitMethod,
  tool,
  fields,
  skipMailOption,
  leoUrl,
  hasRedirectGuidance,
}) {
  const person = useSelector((state) => state.person);
  const stateCode = useStateCode();
  const stateName = useStateName();

  let ovrButtonText = 'Register online (recommended)';
  if (hasRedirectGuidance) {
    const domain = urlToPrimaryDomain(ovrLink);
    ovrButtonText = `Register online at ${domain}${
      domain.length <= 12 ? ' (recommended)' : ''
    }`;
  }

  const ovrDisclaimer = useStateField('id_laws_online_registration');
  const ovrDirections = useStateField('registration_directions_online');

  const handleExternalClick = () => {
    sendEvent(fields.action_id, 'FinishExternal', tool);

    updateDataLayer({
      event: 'action-finish',
      method: 'external',
      tool,
      url: ovrLink,
      state: stateCode,
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email,
      zipcode: person.zipcode,
    });

    toggleClickExternal(true);
  };

  const leoClick = () => {
    sendEvent(fields.action_id, 'FinishLEOReferral', tool);

    updateDataLayer({
      event: 'action-finish',
      method: 'leo-referral',
      tool,
      state: person.state,
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email,
      zipcode: person.zipcode,
      url: leoUrl,
    });

    navigate(leoUrl);
  };

  if (ovrLink) {
    return (
      <ContentDiv>
        {tool === 'futurevoter' ? (
          <FuturevoterEligibleHeader displayGoodNews />
        ) : (
          <>
            <SameDayRegistrationAlert />
            <h1>How would you like to register to vote?</h1>
          </>
        )}

        {tool === 'futurevoter' && <FuturevoterEligibleContent />}

        {ovrDisclaimer.toUpperCase() !== 'N/A' && (
          <>
            <Markdown content={ovrDisclaimer} />
            {skipMailOption && (
              <p>
                If you don&apos;t have the required identification, you can
                contact your election office.
              </p>
            )}
          </>
        )}

        {ovrDirections && (
          <>
            <h2>Online voter registration instructions</h2>
            <Markdown content={ovrDirections} />
          </>
        )}

        {hasRedirectGuidance && (
          <p>
            If you choose to register online, we will open the {stateName} voter
            registration website in a new tab.
          </p>
        )}

        <ButtonGroup>
          <CustomizableButton
            href={ovrLink}
            target='_blank'
            id={`${tool}-finish-state-redirect`}
            rel='noopener'
            onClick={handleExternalClick}
            data-element-id={`btn_${tool}_proceed-with-state_register-online`}
            elementType='a'
          >
            {ovrButtonText}
            <ExternalLinkIcon />
          </CustomizableButton>

          {skipMailOption ? (
            <CustomizableButton
              onClick={leoClick}
              data-element-id='btn_futurevoter-mail-unavailable_lookup'
              isSecondary
              elementType='button'
            >
              Contact your local election office
            </CustomizableButton>
          ) : (
            <CustomizableButton
              isSecondary
              onClick={() => setSubmitMethod('print_mail')}
              data-element-id={`btn_${tool}_proceed-with-state_print-mail`}
              elementType='button'
            >
              Register by mail
            </CustomizableButton>
          )}
        </ButtonGroup>
      </ContentDiv>
    );
  }

  return null;
}

ProceedWithStateContent.propTypes = {
  ovrLink: PropTypes.string,
  toggleClickExternal: PropTypes.func.isRequired,
  setSubmitMethod: PropTypes.func.isRequired,
  tool: PropTypes.oneOf(['register', 'futurevoter']),
  fields: PropTypes.object,
  skipMailOption: PropTypes.bool,
  leoUrl: PropTypes.string,
  hasRedirectGuidance: PropTypes.bool,
};

ProceedWithStateContent.defautProps = {
  skipMailOption: false,
};

function ClickedExternalContent({
  ovrLink,
  setSubmitMethod,
  tool,
  fields,
  successUrl,
  skipMailOption,
  leoUrl,
}) {
  const stateName = useStateName();
  const person = useSelector((state) => state.person);

  const handleConfirmation = () => {
    sendEvent(fields.action_id, 'FinishExternalConfirmed');
    updateDataLayer({
      event: 'action-follow-up',
      method: 'external-confirmed',
      tool,
      url: ovrLink,
      state: person.state,
      first_name: person.first_name,
      last_name: person.last_name,
      email: person.email,
      zipcode: person.zipcode,
    });
  };

  return (
    <ContentDiv>
      <h1>{`We just opened the ${stateName} online voter registration website in another tab`}</h1>
      <p>
        You will need to follow{' '}
        <CustomizableLink
          href={ovrLink}
          title={`${stateName} voter registration workflow`}
          isExternal
        >
          the workflow
        </CustomizableLink>{' '}
        and fill out all the required fields. Come back here when you&apos;re
        done!
      </p>
      <p>
        If anything goes wrong,{' '}
        {skipMailOption ? (
          <>you can always contact your local election office.</>
        ) : (
          <>you can still print and mail a paper form.</>
        )}
      </p>
      <ButtonGroup>
        <CustomizableButton
          to={successUrl}
          state={{ variant: 'info' }}
          onClick={handleConfirmation}
          elementType='Link'
        >
          I finished registering online!
        </CustomizableButton>
        {skipMailOption ? (
          <CustomizableButton
            isSecondary
            href={leoUrl}
            target='_blank'
            rel='noopener'
            data-element-id='btn_futurevoter-mail-unavailable_lookup'
            elementType='a'
          >
            Contact your local election office
          </CustomizableButton>
        ) : (
          <CustomizableButton
            isSecondary
            onClick={() => setSubmitMethod('print_mail', true)}
            data-element-id={`btn_${tool}_proceed-with-state_print-mail`}
            elementType='button'
          >
            Print and mail a paper form instead
          </CustomizableButton>
        )}
      </ButtonGroup>
    </ContentDiv>
  );
}

ClickedExternalContent.propTypes = {
  ovrLink: PropTypes.string.isRequired,
  setSubmitMethod: PropTypes.func.isRequired,
  tool: PropTypes.oneOf(['register', 'futurevoter']),
  fields: PropTypes.object,
  successUrl: PropTypes.string,
  skipMailOption: PropTypes.bool,
  leoUrl: PropTypes.string,
};

ClickedExternalContent.propTypes = {
  skipMailOption: false,
};

function ProceedWithState({ tool, successPage, hasRedirectGuidance }) {
  const stateCode = useStateCode();
  const dispatch = useDispatch();

  const registerFields = useSelector((state) => state.registerFields);
  const futurevoterFields = useSelector((state) => state.futurevoterFields);
  const fields = tool === 'futurevoter' ? futurevoterFields : registerFields;
  const ovrLink = fields.ovr_link;
  const leoUrl = fields.region
    ? `/local-election-offices/${stateCode}/${fields.region}/`
    : `/local-election-offices/${stateCode}/`;
  const alertsRegister = useStateField('alerts_registration');
  const warningsRegister = useStateField('warnings_registration');

  const skipMailOption =
    tool === 'futurevoter' &&
    registrationNoStateFormAvailable.includes(stateCode) &&
    !futurevoterFields.will_be_18_by_next_election;

  const successUrl =
    successPage === 'donate'
      ? `/tools/${tool}/donate/`
      : `/tools/${tool}/share/`;

  const [clickExternal, toggleClickExternal] = useState(false);

  const setSubmitMethod = (submitMethod, isFollowUp = false) => {
    dispatch({
      type:
        tool === 'futurevoter'
          ? 'FUTUREVOTER_SUBMIT_METHOD'
          : 'REGISTER_SUBMIT_METHOD',
      submitMethod,
    });
    sendRegistrationForm(isFollowUp, tool);
  };

  useRegisterRedirect('proceed-with-state', tool);
  if (!stateCode) {
    return null;
  }

  if (clickExternal) {
    return (
      <>
        {warningsRegister && (
          <AlertBar alertType='warning'>
            <Markdown content={warningsRegister} />
          </AlertBar>
        )}
        {alertsRegister && (
          <AlertBar alertType='info'>
            <Markdown content={alertsRegister} />
          </AlertBar>
        )}
        <ClickedExternalContent
          ovrLink={ovrLink}
          setSubmitMethod={setSubmitMethod}
          tool={tool}
          fields={fields}
          successUrl={successUrl}
          skipMailOption={skipMailOption}
          leoUrl={leoUrl}
        />
      </>
    );
  }

  return (
    <>
      {warningsRegister && (
        <AlertBar alertType='warning'>
          <Markdown content={warningsRegister} />
        </AlertBar>
      )}
      {alertsRegister && (
        <AlertBar alertType='info'>
          <Markdown content={alertsRegister} />
        </AlertBar>
      )}
      <ProceedWithStateContent
        toggleClickExternal={toggleClickExternal}
        ovrLink={ovrLink}
        setSubmitMethod={setSubmitMethod}
        tool={tool}
        fields={fields}
        skipMailOption={skipMailOption}
        leoUrl={leoUrl}
        hasRedirectGuidance={hasRedirectGuidance}
      />
    </>
  );
}

ProceedWithState.propTypes = {
  tool: PropTypes.oneOf(['register', 'futurevoter']),
  successPage: PropTypes.oneOf(['donate', 'share']),
  hasRedirectGuidance: PropTypes.bool,
};

ProceedWithState.defaultProps = {
  tool: 'register',
  hasRedirectGuidance: false,
};

export default ProceedWithState;
